import { subYears } from "date-fns"
import { useMemo } from "react"
import { useAuth0 } from "@auth0/auth0-react"

import ComparisonCard, { type ComparisonCardRow } from "~/components/forecast/ComparisonCard"

import { Card, CardContent, CardEmptyTitle } from "~/components/reusable/Card"
import { useSkuStore } from "~/providers/sku-store-provider"
import { api } from "~/utils/api"
import { startOfTodayUTC } from "~/utils/datetime"
import { dateSeries } from "~/utils/graphUtils"
import { useChartSeriesConfig } from "~/constants/colors"

import SuperChart from "../reusable/graphs/SuperChart"
import ComparisonCardSilhouette from "../reusable/skeletonSilhouette/ComparisonCardSilhouette"
import DoughnutChartSilhouette from "../reusable/skeletonSilhouette/DoughnutCardSilhouette"
import LineGraphSilhouette from "../reusable/skeletonSilhouette/LineGraphSilhouette"
import CollectionStockLevel from "./CollectionStockLevel"
import { type IVariantSummary } from "~/hooks/useVariantTableData"
import { ChartType, DateType } from "~/constants/graphs"

type Props = {
  variants: IVariantSummary[]
  isFiltered?: boolean
  vertical?: boolean
}

const NoData = () => {
  return (
    <Card className="h-[150px]">
      <CardContent className="flex h-full flex-col items-center justify-center">
        <CardEmptyTitle>No SKUs found</CardEmptyTitle>
      </CardContent>
    </Card>
  )
}

const InventoryKeyMetrics = ({ variants, isFiltered = false, vertical = false }: Props) => {
  const { user } = useAuth0()
  const isCarharttWip = user?.org_name === "carhartt_wip"

  // Carhartt keeps its collection-stock card in the second slot; everyone else drops it.
  const containerClassName = vertical
    ? "flex flex-col gap-4"
    : isCarharttWip
      ? "grid grid-cols-[350px_350px_1fr_1fr] gap-4"
      : "grid grid-cols-[350px_1fr_1fr] gap-4"
  const noDataClassName = vertical
    ? "flex flex-col gap-4"
    : isCarharttWip
      ? "grid grid-cols-[1fr_1fr_350px_450px] gap-4"
      : "grid grid-cols-[1fr_350px_450px] gap-4"
  const chartConfig = useChartSeriesConfig()
  const variantIds = useMemo(() => variants.map((p) => p.variantId), [variants])
  // Pass undefined (not all IDs) when unfiltered to let the planner pick a hash join.
  const cogsVariantIds = isFiltered ? variantIds : undefined
  const selectedWarehouseId = useSkuStore((state) => state.warehouseId)

  const startDate = subYears(startOfTodayUTC(), 1)
  const endDate = startOfTodayUTC()

  const { data: inventoryData } = api.product.inventoryValuesLastYear.useQuery(
    { variantIds, warehouseId: selectedWarehouseId },
    { enabled: variantIds.length > 0 },
  )
  const { data: costOfGoodsSoldData } = api.product.costOfGoodsSold.useQuery(
    {
      variantIds: cogsVariantIds,
      startDate,
      endDate,
      warehouseId: selectedWarehouseId,
    },
    { enabled: variants.length > 0 },
  )

  if (variants.length === 0) {
    return (
      <div className={noDataClassName}>
        <NoData />
        {isCarharttWip && <NoData />}
        <NoData />
        <NoData />
      </div>
    )
  }

  if (!inventoryData || !costOfGoodsSoldData) {
    return (
      <div className={containerClassName}>
        <ComparisonCardSilhouette rowsCount={2} horizontal />
        {isCarharttWip && <DoughnutChartSilhouette height={168} />}
        <LineGraphSilhouette height={168} />
        <LineGraphSilhouette height={168} />
      </div>
    )
  }

  const lastInventoryValue = Math.round(inventoryData[inventoryData.length - 1]?.value ?? 0)

  const rows: ComparisonCardRow[] = [
    {
      title: "Inventory value (now)",
      value: lastInventoryValue,
      currency: true,
    },
    {
      title: "Units in stock",
      value: variants.reduce((acc, product) => acc + (product.stockLevel ?? 0), 0),
    },
  ]

  const cogsByMonth = new Map(
    costOfGoodsSoldData.map(({ date, value }) => [
      `${date.getUTCFullYear()}-${date.getUTCMonth()}`,
      value,
    ]),
  )

  const inventoryTurnSeries = dateSeries(chartConfig.closingStock, {
    name: "Inventory Turn",
    data: inventoryData.map(({ date, value }) => {
      const cogs = cogsByMonth.get(`${date.getUTCFullYear()}-${date.getUTCMonth()}`) ?? 0
      return {
        milliseconds: date.getTime(),
        value: value > 0 ? (cogs * 12) / value : 0,
      }
    }),
    type: "areaspline",
  })

  // Both series read closing stock — valued at cost, and turned over against COGS.
  const inventoryValueSeries = dateSeries(chartConfig.closingStock, {
    name: "Inventory value",
    data: inventoryData.map(({ date, value }) => ({
      milliseconds: date.getTime(),
      value: Math.round(value),
    })),
    type: "areaspline",
  })

  const chartHeight = vertical ? 180 : 126
  const cardClassName = vertical ? "w-full" : ""

  return (
    <div className={containerClassName}>
      <ComparisonCard rows={rows} horizontal className={vertical ? "w-full" : "h-full"} />
      {isCarharttWip && <CollectionStockLevel variants={variants} />}
      <Card className={cardClassName}>
        <CardContent className="flex flex-col">
          <SuperChart
            type={ChartType.TimeSeries}
            dateType={DateType.Monthly}
            height={chartHeight}
            title="Inventory Value"
            data={[inventoryValueSeries]}
            noLegend
            showCurrency
          />
        </CardContent>
      </Card>
      <Card className={cardClassName}>
        <CardContent className="flex flex-col">
          <SuperChart
            type={ChartType.TimeSeries}
            dateType={DateType.Monthly}
            height={chartHeight}
            title="Inventory Turn"
            data={[inventoryTurnSeries]}
            noLegend
          />
        </CardContent>
      </Card>
    </div>
  )
}
export default InventoryKeyMetrics
